import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../Components/Header";
import { Link } from "react-router-dom";
import Footer from "../Components/Footer";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./BlogDetailsLayouts.css";


function formatDate(dateStr) {
  try {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  } catch {
    return dateStr || "";
  }
}

function slugifyId(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .replace(/<[^>]*>/g, "")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function buildTocAndHtml(html) {
  if (typeof window === "undefined" || !html) {
    return { html: html || "", toc: [] };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(html), "text/html");
    const headings = Array.from(doc.querySelectorAll("h2, h3"));

    const toc = [];
    const used = new Set();

    headings.forEach((h) => {
      const level = h.tagName.toLowerCase() === "h2" ? 2 : 3;
      const text = (h.textContent || "").trim();
      if (!text) return;

      let id = h.getAttribute("id") || slugifyId(text);
      if (!id) return;
      let candidate = id;
      let i = 2;
      while (used.has(candidate)) {
        candidate = `${id}-${i}`;
        i += 1;
      }
      id = candidate;
      used.add(id);
      h.setAttribute("id", id);

      toc.push({ id, text, level });
    });

    return { html: doc.body.innerHTML, toc };
  } catch {
    return { html: html || "", toc: [] };
  }
}

function ShareRow({ title }) {
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: title || document.title, url });
        return;
      }
    } catch {
      // ignore
    }
    try {
      await navigator.clipboard.writeText(url);
      // simple fallback UX without extra deps
      alert("Link copied");
    } catch {
      alert(url);
    }
  };

  return (
    <div className="bd-share">
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={share}>
        Share
      </button>
    </div>
  );
}

function MorePostsSlider({ blogs, title = "More posts" }) {
  const items = (blogs || []).slice(0, 10);
  if (!items.length) return null;

  const infinite = items.length > 3;
  const settings = {
    dots: false,
    arrows: false,
    infinite,
    autoplay: items.length > 1,
    autoplaySpeed: 4500,
    speed: 650,
    slidesToShow: Math.min(3, items.length),
    slidesToScroll: 1,
    adaptiveHeight: false,
    pauseOnHover: true,
    pauseOnFocus: true,
    swipeToSlide: true,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: Math.min(2, items.length),
          slidesToScroll: 1,
          infinite: items.length > 2,
          autoplay: items.length > 1,
        },
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: items.length > 1,
          autoplay: items.length > 1,
        },
      },
    ],
  };

  return (
    <div className="bd-related">
      <div className="bd-related__title">{title}</div>
      <div className="bd-related__slider">
        <Slider {...settings}>
          {items.map((blog, idx) => (
            <div key={blog.slug || idx} className="bd-related__slide">
              <div className="single-blog-card card gray-light-bg border-0 shadow-sm my-3 blogs-modern-card">
                <div className="blog-img position-relative">
                  <img src={`${blog.image}`} className="card-img-top" alt={blog.title} />
                  <div className="meta-date">
                    <strong>{new Date(blog.date).getDate()}</strong>
                    <small>
                      {new Date(blog.date).toLocaleString("default", {
                        month: "short",
                      })}
                    </small>
                  </div>
                </div>
                <div className="card-body">
                  <h3 className="h5 mb-2 card-title">
                    <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                  </h3>
                  <p className="card-text">{blog.desc}</p>
                  <Link to={`/blog/${blog.slug}`} className="detail-link">
                    Read more <i className="fas fa-arrow-right" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
}

function MorePostsGrid({ blogs, title = "More posts" }) {
  const items = (blogs || []).slice(0, 6);
  if (!items.length) return null;
  return (
    <div className="bd-related">
      <div className="bd-related__title">{title}</div>
      <div className="row">
        {items.map((blog, idx) => (
          <div key={blog.slug || idx} className="col-md-6 col-lg-4">
            <div className="single-blog-card card gray-light-bg border-0 shadow-sm my-3 blogs-modern-card">
              <div className="blog-img position-relative">
                <img src={`${blog.image}`} className="card-img-top" alt={blog.title} />
                <div className="meta-date">
                  <strong>{new Date(blog.date).getDate()}</strong>
                  <small>
                    {new Date(blog.date).toLocaleString("default", {
                      month: "short",
                    })}
                  </small>
                </div>
              </div>
              <div className="card-body">
                <h3 className="h5 mb-2 card-title">
                  <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                </h3>
                <p className="card-text">{blog.desc}</p>
                <Link to={`/blog/${blog.slug}`} className="detail-link">
                  Read more <i className="fas fa-arrow-right" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MorePostsList({ blogs, title = "More posts" }) {
  const items = (blogs || []).slice(0, 8);
  if (!items.length) return null;
  return (
    <div className="bd-related">
      <div className="bd-related__title">{title}</div>
      <div className="bd-related__list">
        {items.map((blog, idx) => (
          <Link key={blog.slug || idx} to={`/blog/${blog.slug}`} className="bd-related__listItem">
            <div className="bd-related__listTitle">{blog.title}</div>
            <div className="bd-related__listMeta">{formatDate(blog.date)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function MorePostsMarquee({ blogs, title = "More posts" }) {
  const items = (blogs || []).slice(0, 10);
  if (!items.length) return null;
  // Duplicate list for seamless loop
  const loop = items.length >= 3 ? [...items, ...items] : items;
  return (
    <div className="bd-related">
      <div className="bd-related__title">{title}</div>
      <div className="bd-marquee" aria-label="More posts auto scroll">
        <div className="bd-marquee__track">
          {loop.map((blog, idx) => (
            <div key={`${blog.slug || "x"}-${idx}`} className="bd-marquee__item">
              <Link to={`/blog/${blog.slug}`} className="bd-marquee__card">
                <img src={blog.image} alt={blog.title} className="bd-marquee__img" />
                <div className="bd-marquee__body">
                  <div className="bd-marquee__meta">{formatDate(blog.date)}</div>
                  <div className="bd-marquee__title">{blog.title}</div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function BlogDetails() {
  const { slug } = useParams();
  const [blogDetail, setBlogDetail] = useState(null);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    fetch("/blogs.json")
      .then((response) => response.json())
      .then((data) => {
        const foundBlog = data.blogDetails.find((blog) => blog.slug === slug);
        setBlogDetail(foundBlog);
      })
      .catch((error) => console.error("Error fetching blogs:", error));

    fetch("/blogs.json")
      .then((response) => response.json())
      .then((data) => {
        const filterBlogs = data.blogs.filter((blog) => blog.slug !== slug);
        setBlogs(filterBlogs);
      })
      .catch((error) => console.error("Error fetching blogs:", error));
  }, [slug]);

  const { html: detailHtml } = useMemo(() => buildTocAndHtml(blogDetail?.detailDesc), [blogDetail?.detailDesc]);
  const related = useMemo(() => (blogs || []).slice(0, 6), [blogs]);

  if (!blogDetail) {
    return <p>Loading...</p>;
  }

  return (
    <div>
      <Header />
      {/* Layout B (final): cover banner + wide article + more posts */}
      <div className="bd-cover" style={{ backgroundImage: `url('${blogDetail.image}')` }}>
        <div className="bd-cover__overlay" />
        <div className="container bd-cover__inner">
          <h1 className="bd-cover__title">{blogDetail.title}</h1>
          <div className="bd-cover__headrow">
            <div className="bd-cover__meta">{formatDate(blogDetail.date)}</div>
            <div className="bd-cover__actions">
              <ShareRow title={blogDetail.title} />
            </div>
          </div>
        </div>
      </div>
      <div className="module ptb-100 bd-b-body">
        <div className="container-fluid bd-wide-container">
          <div className="row justify-content-center">
            <div className="col-12 col-xl-10">
              <div className="bd-article">
                <div className="post-content">
                  <div dangerouslySetInnerHTML={{ __html: detailHtml }} />
                </div>
              </div>
              <MorePostsMarquee blogs={related} title="More posts" />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default BlogDetails;
